import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElementLayout,
} from '@anchorage/graphql/generated';
import { KYCQuestionType } from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { parseSanitize } from '@anchorage/utils';
import { reactHookForm } from '@latitude/form';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { Tooltip } from '@latitude/tooltip';
import cn from 'classnames';
import { type FormAnswer } from 'hooks/useQuestionnaire/types';
import { useQuestionnaire } from 'hooks/useQuestionnaire/useQuestionnaire';
import { relatedQuestionAnsweredOptionIds } from 'hooks/useQuestionnaire/utils';
import type { ComponentType } from 'react';
import { useEffect, useMemo } from 'react';

import { QuestionNote } from '../QuestionNote';
import { CardListComponent } from './components/CardListComponent';
import { CheckboxComponent } from './components/CheckboxComponent';
import { DateComponent } from './components/DateComponent';
import { DropdownComponent } from './components/DropdownComponent';
import { RadioComponent } from './components/RadioComponent';
import { RadioGroupComponent } from './components/RadioGroupComponent';
import { TextareaComponent } from './components/TextareaComponent';
import { TextFieldComponent } from './components/TextFieldComponent';
import { UploadComponent } from './components/UploadComponent/UploadComponent';

import css from './styles.module.css';

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  answeredOptions?: Record<string, string>;
  groupNameAndIndex?: string;
  CustomLink?: ComponentType<CustomLinkProps>;
  isBackoffice?: boolean;
  layout?: KYCQuestionnaireElementLayout;
  allQuestions?: KYCQuestionDefinition[];
  trackFn?: (questionId: string) => void;
  showTriggered?: boolean;
};

function Question({
  question,
  isDisabled = false,
  answeredOptions,
  groupNameAndIndex,
  CustomLink,
  isBackoffice,
  layout,
  allQuestions,
  trackFn = () => {},
  showTriggered = false,
}: Props) {
  const { useFormContext } = reactHookForm;
  const { setValue, getValues } = useFormContext();
  const { shouldShowElement } = useQuestionnaire();

  const shouldShow = useMemo(
    () =>
      shouldShowElement?.({
        triggerOptionID: question.triggerOptionID,
        answeredOptions,
        questions: allQuestions,
        groupNameAndIndex,
      }),
    [
      question.triggerOptionID,
      shouldShowElement,
      answeredOptions,
      groupNameAndIndex,
      allQuestions,
    ],
  );

  const isValidQuestionType = useMemo(
    () =>
      [
        KYCQuestionType.CHECKBOX,
        KYCQuestionType.DATE,
        KYCQuestionType.DROPDOWN,
        KYCQuestionType.DROPDOWN_MULTI_SELECT,
        KYCQuestionType.GROUP,
        KYCQuestionType.RADIO,
        KYCQuestionType.RADIO_GROUP,
        KYCQuestionType.TEXT_AREA,
        KYCQuestionType.UPLOAD,
        KYCQuestionType.TEXTFIELD,
        KYCQuestionType.CARD_SELECT,
      ].includes(question.type),
    [question.type],
  );

  const questionComponent = useMemo(() => {
    switch (question.type) {
      case KYCQuestionType.CHECKBOX:
        return (
          <CheckboxComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.DATE:
        return (
          <DateComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
          />
        );
      case KYCQuestionType.DROPDOWN:
        return (
          <DropdownComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            isBackoffice={isBackoffice}
            freeformOptionId={question.freeformOptionID}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.DROPDOWN_MULTI_SELECT:
        return (
          <DropdownComponent
            isDisabled={isDisabled || question.readOnly}
            isMulti
            question={question}
            isBackoffice={isBackoffice}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.RADIO:
        return (
          <RadioComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.RADIO_GROUP:
        return (
          <RadioGroupComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
          />
        );
      case KYCQuestionType.TEXT_AREA:
        return (
          <TextareaComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            textAreaSize={layout?.textAreaSize}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.UPLOAD:
        return (
          <UploadComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            isBackoffice={isBackoffice}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.CARD_SELECT:
        return (
          <CardListComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            trackFn={trackFn}
          />
        );
      default:
        return (
          <TextFieldComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
    }
  }, [
    question,
    isDisabled,
    CustomLink,
    isBackoffice,
    layout?.textAreaSize,
    trackFn,
  ]);

  // if this question has relatedQuestionIDs, we want to copy the answer of this question to the related questions
  useEffect(() => {
    if (shouldShow) {
      const relatedQuestionIDs =
        (question.relatedQuestionIDs as string[]) || [];
      const answer = getValues(question.id) as FormAnswer;
      const answeredOptionIds: string[] | string = answer?.options || '';

      // we go through every related question and copy the answer of this question to the related question
      // if it has a valid equivalent
      relatedQuestionIDs.forEach((relatedQuestionID) => {
        let relatedQuestionAnswer = answer;

        // if there is answered options, we need to filter the invalid options of the related question
        if (answeredOptionIds.length) {
          const relatedQuestionPossibleOptions =
            allQuestions?.find((q) => q.id === relatedQuestionID)?.options ||
            [];

          const relatedQuestionOptions = relatedQuestionAnsweredOptionIds(
            answeredOptionIds,
            question.options,
            relatedQuestionPossibleOptions,
          );

          relatedQuestionAnswer = {
            ...answer,
            options: relatedQuestionOptions,
          };

          // we only copy the answer if the related question has equivalent options
          // if not, we leave it as is
          relatedQuestionOptions.length &&
            setValue(`${relatedQuestionID}`, relatedQuestionAnswer);
        } else {
          setValue(`${relatedQuestionID}`, relatedQuestionAnswer);
        }
      });
    }
  }, [
    shouldShow,
    question.id,
    setValue,
    question.relatedQuestionIDs,
    getValues,
    allQuestions,
    question.options,
  ]);

  if (!shouldShow && !showTriggered) {
    return null;
  }

  if (!isValidQuestionType) {
    return (
      <div className={css.container}>
        <Text color="danger">Error reading the questionnaire</Text>
      </div>
    );
  }

  return (
    <div
      className={cn(css.container, question.hidden && css.hidden)}
      data-question-id={question.id}
    >
      <div className={css.titleWrapper}>
        {question?.text && (
          <Text variant="bodyMedium" color="strong">
            {question?.text}
          </Text>
        )}
        {question?.tooltip && (
          <Tooltip
            color="light"
            text={<Text variant="captionMedium">{question?.tooltip}</Text>}
            side="bottom"
            className={css.tooltip}
          >
            <Icon
              name="info-circle"
              className="ml-sizing-sys-size-8 h-sizing-sys-size-16 w-sizing-sys-size-16 align-top"
            />
          </Tooltip>
        )}
      </div>
      {question?.header && (
        <Text className={css.header} variant="captionMedium">
          {parseSanitize(question?.header, CustomLink)}
        </Text>
      )}
      {questionComponent}
      {question.optional && (
        <Text className={css.optional} variant="captionMedium">
          Optional
        </Text>
      )}
      {question?.note && <QuestionNote note={question.note} />}
    </div>
  );
}

export { Question };
